import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  DownloadOutlined,
  MailOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {useEffect, useState} from 'react';
import {useQuery} from 'react-query';
import {useSearchParams} from 'react-router-dom';
import api from '../../api';
import MeetingArchiveToggle from '../../components/MeetingArchiveToggle';
import {MainLayout} from '../../containers/Common';
import {formatDate} from '../../utils/normalize';

const {Title, Text} = Typography;

const normalizeFilenamePart = value => {
  const replacements = {
    Ä: 'Ae',
    Ö: 'Oe',
    Ü: 'Ue',
    ä: 'ae',
    ö: 'oe',
    ü: 'ue',
    ß: 'ss',
  };
  return (value || 'BUEV-Export')
    .replace(/[ÄÖÜäöüß]/g, match => replacements[match])
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'BUEV-Export';
};

const memberUrl = (meeting, params = {}) => {
  const query = new URLSearchParams({meeting, ...params});
  return `/?${query.toString()}`;
};

const invitationUrl = (meeting, params = {}) => {
  const query = new URLSearchParams({meeting, ...params});
  return `/invitation?${query.toString()}`;
};

const DashboardCard = ({title, value, suffix, tone, href}) => {
  const content = <Card className={`dashi-card ${tone ? `dashi-card--${tone}` : ''}`} hoverable={!!href}>
    <Statistic title={title} value={value ?? '-'} suffix={suffix}/>
  </Card>;
  if (!href) {
    return content;
  }
  return <a className="dashi-card-link" href={href}>{content}</a>;
};

const Dashboard = () => {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [meetingId, setMeetingId] = useState(
    urlSearchParams.get('meeting') ? Number(urlSearchParams.get('meeting')) : null
  );
  const [showArchived, setShowArchived] = useState(urlSearchParams.get('archived') === '1');

  const {data: {data: meetings = []} = {}} = useQuery(
    ['dashi-meetings', showArchived],
    () => api.meetings.meetingList(showArchived ? {archive_only: true} : {}),
    {cacheTime: Infinity},
  );

  useEffect(() => {
    if (!meetings.length) {
      if (meetingId) {
        setMeetingId(null);
      }
      return;
    }
    if (!meetingId || !meetings.some(item => item.id === meetingId)) {
      setMeetingId(meetings[0].id);
    }
  }, [meetings, meetingId]);

  useEffect(() => {
    if (meetingId) {
      const nextParams = {meeting: meetingId};
      if (showArchived) {
        nextParams.archived = '1';
      }
      setUrlSearchParams(nextParams);
    }
  }, [meetingId, setUrlSearchParams, showArchived]);

  const {data: {data: dashboard} = {}, isLoading} = useQuery(
    ['dashi-dashboard', meetingId, showArchived],
    () => api.meetings.meetingDashboard(
      meetingId,
      showArchived ? {include_archived: true} : {},
    ),
    {enabled: !!meetingId},
  );

  const meeting = dashboard?.meeting;
  const meetingFilename = normalizeFilenamePart(meeting?.name);
  const reportParams = {meeting: meetingId};

  const toggleArchived = (value) => {
    setShowArchived(value);
    setMeetingId(null);
  };

  const downloadMembersCsv = () => api.report.membersCsv({
    filename: `${meetingFilename}-Teilnehmer.csv`,
    params: reportParams,
  });
  const downloadMembersXlsx = () => api.report.membersXlsx({
    filename: `${meetingFilename}-Teilnehmer.xlsx`,
    params: reportParams,
  });
  const downloadProgramXlsx = () => api.report.programMembersXlsx({
    filename: `${meetingFilename}-Programm.xlsx`,
    params: reportParams,
  });
  const downloadSigns = () => api.report.listOfSigns({
    filename: `unterschriftenliste-${meetingId}.pdf`,
    params: reportParams,
  });
  const downloadBadges = () => api.report.badges({
    filename: `namensschilder-${meetingId}.pdf`,
    params: reportParams,
  });

  const breadCrumbs = [
    {key: 'dashboard', name: 'Dashi'},
  ];

  return <MainLayout breadCrumbs={breadCrumbs}>
    <div className="dashi">
      <Row gutter={[16, 16]} align="middle" className="dashi-head">
        <Col xs={24} lg={12}>
          <Title level={2}>Dashi</Title>
          <Text type="secondary">Veranstaltungsstand, Rückmeldungen und Schnellzugriffe</Text>
        </Col>
        <Col xs={24} lg={12}>
          <Space direction="vertical" style={{width: '100%'}}>
            <MeetingArchiveToggle showArchived={showArchived} onChange={toggleArchived}/>
            <Select
              className="dashi-meeting-select"
              value={meetingId}
              onChange={setMeetingId}
              loading={isLoading}
            >
              {meetings.map(item => <Select.Option key={item.id} value={item.id}>
                {item.is_visible ? item.name : `${item.name} (archiviert)`}
              </Select.Option>)}
            </Select>
          </Space>
        </Col>
      </Row>

      {meeting && <div className="dashi-event">
        <Title level={3}>{meeting.name}</Title>
        <Text type="secondary">
          {[formatDate(meeting.date_from), meeting.area].filter(Boolean).join(' · ')}
          {!meeting.is_visible && ' · Archiviert'}
        </Text>
      </div>}

      {dashboard && <>
        <Row gutter={[16, 16]} className="dashi-card-row">
          <Col xs={24} md={12} xl={6}>
            <DashboardCard
              title="Eingeladen"
              value={dashboard.responses.invitations_total}
              href={invitationUrl(meetingId)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <DashboardCard
              title="Zusagen"
              value={dashboard.responses.confirmed}
              tone="good"
              href={memberUrl(meetingId, {response_status: 'confirmed'})}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <DashboardCard
              title="Absagen"
              value={dashboard.responses.declined}
              tone="warning"
              href={memberUrl(meetingId, {response_status: 'declined'})}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <DashboardCard
              title="Offen"
              value={dashboard.responses.open}
              tone={dashboard.responses.open ? 'attention' : ''}
              href={memberUrl(meetingId, {response_status: 'open'})}
            />
          </Col>
        </Row>

        {dashboard.capacity.enabled && <Row gutter={[16, 16]} className="dashi-card-row">
          <Col xs={24} md={8}>
            <DashboardCard title="Reguläre Plätze" value={dashboard.capacity.max_regular_participants}/>
          </Col>
          <Col xs={24} md={8}>
            <DashboardCard
              title="Regulär angemeldet"
              value={dashboard.capacity.regular_participants}
              href={memberUrl(meetingId, {response_status: 'confirmed'})}
            />
          </Col>
          <Col xs={24} md={8}>
            <DashboardCard
              title={dashboard.capacity.free_places > 0 ? 'Freie Plätze' : 'Teilnehmerzahl erreicht'}
              value={dashboard.capacity.free_places}
              tone={dashboard.capacity.free_places > 0 ? 'good' : 'attention'}
            />
          </Col>
        </Row>}

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Card title="Rückmeldung" className="dashi-panel">
              <Space wrap>
                {dashboard.status_breakdown.map(item => <Button
                  key={item.status_id}
                  href={memberUrl(meetingId, {status: item.status_id})}
                >
                  {item.name}: {item.members + item.open}
                </Button>)}
                {dashboard.responses.waitlist > 0 && <Button href={memberUrl(meetingId, {response_status: 'waitlist'})}>
                  Warteliste: {dashboard.responses.waitlist}
                </Button>}
              </Space>
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="E-Mail-Versand" className="dashi-panel">
              <Row gutter={[12, 12]}>
                <Col span={8}>
                  <a href={invitationUrl(meetingId, {email_status: 'success'})}>
                    <Statistic title="Erfolgreich" value={dashboard.email.invitation_success}/>
                  </a>
                </Col>
                <Col span={8}>
                  <a href={invitationUrl(meetingId, {email_status: 'failed'})}>
                    <Statistic title="Fehler" value={dashboard.email.invitation_failed} valueStyle={{color: dashboard.email.invitation_failed ? '#b42318' : undefined}}/>
                  </a>
                </Col>
                <Col span={8}>
                  <a href={invitationUrl(meetingId, {email_status: 'not_sent'})}>
                    <Statistic title="Nicht versendet" value={dashboard.email.invitation_not_sent}/>
                  </a>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="Get-Together" className="dashi-panel">
              <Space size="large" wrap>
                <Statistic title="Mitglieder" value={dashboard.get_together.members}/>
                <Statistic title="Begleitpersonen" value={dashboard.get_together.guests}/>
                <Statistic title="Personen insgesamt" value={dashboard.get_together.total}/>
              </Space>
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="Rechnungen" className="dashi-panel">
              <Space size="large" wrap>
                <Statistic title="Gesamt" value={dashboard.invoices.total}/>
                <Statistic title="Versendet" value={dashboard.invoices.sent}/>
                <Statistic title="Fehler" value={dashboard.invoices.failed} valueStyle={{color: dashboard.invoices.failed ? '#b42318' : undefined}}/>
              </Space>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="dashi-section">
          <Col xs={24} xl={12}>
            <Card title="Dokumente und Schnellzugriffe" className="dashi-panel">
              <Space wrap>
                <Button icon={<TeamOutlined/>} href={memberUrl(meetingId)}>Teilnehmer verwalten</Button>
                <Button icon={<MailOutlined/>} href={invitationUrl(meetingId)}>Einladungen verwalten</Button>
                <Button icon={<DownloadOutlined/>} onClick={downloadSigns}>Unterschriftenliste</Button>
                <Button icon={<DownloadOutlined/>} onClick={downloadBadges}>Namensschilder</Button>
                <Button icon={<DownloadOutlined/>} onClick={downloadMembersCsv}>CSV</Button>
                <Button icon={<DownloadOutlined/>} onClick={downloadMembersXlsx}>XLSX</Button>
                <Button icon={<DownloadOutlined/>} onClick={downloadProgramXlsx}>Programmexport</Button>
              </Space>
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="Aktivität und Anwesenheit" className="dashi-panel">
              <Space direction="vertical" size="middle">
                <Alert
                  type="info"
                  showIcon
                  message="Tischunterlagen-Tracking noch nicht aktiv"
                  description="Die Tischunterlagen liegen aktuell auf buev.eu. Für echte Aufrufe braucht Dashi später einen Tool-Link oder Redirect, der nur Meeting, Einladung/Teilnehmer, Event-Typ und Zeitpunkt speichert."
                />
                <Alert
                  type="info"
                  showIcon
                  message="Anwesenheit vorbereitet als Folgestufe"
                  description="Aktuell gibt es keine Anwesenheits- oder Unterschriftsereignisse im Datenmodell. Dashi zeigt deshalb noch keine erfundenen Zahlen."
                />
              </Space>
            </Card>
          </Col>
        </Row>

        <Card title="Statusübersicht" className="dashi-panel dashi-section">
          <Row gutter={[12, 12]}>
            {dashboard.status_breakdown.map(item => <Col xs={24} md={12} xl={8} key={item.status_id}>
              <div className="dashi-status-row">
                <strong>{item.name}</strong>
                <Space wrap>
                  <Tag>Mitglieder: {item.members}</Tag>
                  <Tag>Offen: {item.open}</Tag>
                  {item.waitlist > 0 && <Tag color="gold">Warteliste: {item.waitlist}</Tag>}
                </Space>
              </div>
            </Col>)}
          </Row>
        </Card>
      </>}

      {!dashboard && !isLoading && <Alert type="info" message="Bitte Veranstaltung auswählen."/>}
    </div>
  </MainLayout>;
};

export default Dashboard;
